import React, { useCallback, useRef, useMemo } from 'react'
import _ from 'lodash'
import { useFieldArray } from 'react-hook-form'

import { useGlobalShortcut } from 'admin/lib/shortcuts'
import { Matchers } from 'admin/lib/Records'

/**
 * @param {import("react-hook-form").UseFormMethods} form
 * @param {string} fieldName
 */
export function useLineItemsManagement(
  form,
  fieldName,
  rawFieldName,
  defaultLineItem,
  defaultRawLineItem = {}
) {
  const methods = useFieldArray({
    name: fieldName,
    keyName: 'hookFormId'
  })

  const rawMethods = useFieldArray({
    name: rawFieldName,
    keyName: 'hookFormId'
  })

  //
  // Construct Data & Refs

  const lineItemsLength = methods.fields.length
  const lineItemMappableIndexes = useMemo(() => {
    return _.range(0, lineItemsLength)
  }, [lineItemsLength])

  const lineItemsRefs = useMemo(
    () => lineItemMappableIndexes.map(() => React.createRef()),
    [lineItemMappableIndexes]
  )

  const itemAppendedRef = useRef(false)

  // Callbacks

  const appendLineItem = useCallback(
    (data = defaultLineItem, raw = defaultRawLineItem) => {
      itemAppendedRef.current = true
      methods.append(data, false)
      rawMethods.append(raw, false)
    },
    [defaultLineItem, defaultRawLineItem, methods, rawMethods]
  )

  const handleAppendLineItem = useMemo(
    () =>
      _.debounce(
        () => {
          appendLineItem(defaultLineItem, defaultRawLineItem)
        },
        750,
        { trailing: true }
      ),
    [appendLineItem, defaultLineItem, defaultRawLineItem]
  )

  const handleAppendLineItemNoDebounce = useCallback(() => {
    appendLineItem(defaultLineItem, defaultRawLineItem)
  }, [appendLineItem, defaultLineItem, defaultRawLineItem])

  const handleAppendLineItemAndScroll = useCallback(() => {
    const inputNameMatcher = window.location.pathname.includes('Delivery')
      ? 'raw_name'
      : 'raw_containerType'
    const elements = document.querySelectorAll('.LineItem')

    handleAppendLineItem()
    focusOnNewItem(elements.length, inputNameMatcher)
  }, [handleAppendLineItem])

  const focusOnNewItem = (index, matcher) => {
    // focus on new element when ready
    const waitForInterval = setInterval(() => {
      const element = document.querySelector(
        `textarea[name^="uniquePrefix[${index}].${matcher}"`
      )
      if (element !== null) {
        clearInterval(waitForInterval)
        element.focus()
        const form = document.getElementsByTagName('form')[0]
        const scrollTop = form.scrollHeight - (matcher === 'name' ? 850 : 0)
        form.scrollTo({
          top: scrollTop,
          behavior: 'smooth'
        })
      }
    }, 500)
  }

  const lineItemDuplicateHandlers = item => {
    const tempItem = { ...item }
    delete tempItem['id']
    delete tempItem['hookFormId']
    delete tempItem['isDeleted']
    delete tempItem['raw_id']
    delete tempItem['raw_hookFormId']
    delete tempItem['raw_isDeleted']

    // Remove item keys that do not start with 'raw_'
    const itemKeysWithRawValues = Object.keys(item).filter(key =>
      key.startsWith('raw_')
    )

    const tempRawItem = {}
    itemKeysWithRawValues.forEach(key => {
      tempRawItem[key.substring(4)] = item[key]
    })

    delete tempRawItem['id']
    delete tempRawItem['hookFormId']
    delete tempRawItem['isDeleted']

    appendLineItem(tempItem, tempRawItem)
  }

  const lineItemRemoveHandlers = useMemo(
    () =>
      lineItemMappableIndexes.map(i => {
        return () => {
          methods.remove(i)
          rawMethods.remove(i)
        }
      }),
    [lineItemMappableIndexes, methods, rawMethods]
  )

  const handleToggleUnit = () => {
    const unitIdStartsWith = document.activeElement.name
    const reletedUnitElement = document.querySelector(
      `[id^='${unitIdStartsWith}']`
    )
    if (reletedUnitElement) {
      reletedUnitElement.click()
    }
  }

  useGlobalShortcut(Matchers.lineItems.add, handleAppendLineItemAndScroll)

  useGlobalShortcut(Matchers.lineItems.toggleUnit, handleToggleUnit)

  return {
    methods,
    rawMethods,
    handleAppendLineItem,
    handleAppendLineItemNoDebounce,
    lineItemsRefs,
    lineItemDuplicateHandlers,
    lineItemRemoveHandlers
  }
}
